import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Download, Eye, CheckCircle, XCircle, Clock,
  Star, MapPin, Phone, Mail, Linkedin, FileText, Filter,
  TrendingUp, Users
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApplicantsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('match_score'); // match_score, date
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchJobAndApplicants();
  }, [isAuthenticated, jobId]);

  const fetchJobAndApplicants = async () => {
    setLoading(true);
    try {
      // Fetch job details
      const jobResponse = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (jobResponse.ok) {
        const jobData = await jobResponse.json();
        setJob(jobData.job);
      }

      // Fetch applications
      const appResponse = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (appResponse.ok) {
        const appData = await appResponse.json();
        setApplicants(appData.applications || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load applicants',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResume = async (applicationId, filename) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/applications/${applicationId}/resume`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'resume.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: 'Downloaded',
          description: 'Resume downloaded successfully',
        });
      } else {
        toast({
          title: 'Error',
          description: 'No resume available',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast({
        title: 'Error',
        description: 'Failed to download resume',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      
      const response = await fetch(`${BACKEND_URL}/api/jobs/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Status updated successfully',
        });
        fetchJobAndApplicants(); // Refresh
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  const getFilteredAndSortedApplicants = () => {
    let filtered = [...applicants];
    
    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }
    
    // Sort
    if (sortBy === 'match_score') {
      filtered.sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
    } else if (sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.applied_at) - new Date(a.applied_at));
    }
    
    return filtered;
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    return 'text-orange-600 bg-orange-100';
  };

  const getMatchScoreLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Review Required';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { color: 'bg-blue-100 text-blue-700', label: 'New' },
      'reviewing': { color: 'bg-yellow-100 text-yellow-700', label: 'Reviewing' },
      'shortlisted': { color: 'bg-green-100 text-green-700', label: 'Shortlisted' },
      'rejected': { color: 'bg-red-100 text-red-700', label: 'Rejected' },
      'hired': { color: 'bg-purple-100 text-purple-700', label: 'Hired' }
    };
    
    const config = statusConfig[status] || statusConfig['new'];
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  const filteredApplicants = getFilteredAndSortedApplicants();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading applicants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            onClick={() => navigate('/my-jobs')} 
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to My Jobs
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Applicants
              </h1>
              {job && (
                <p className="text-lg text-slate-600">
                  {job.title} at {job.company_name}
                </p>
              )}
            </div>
            
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{applicants.length}</div>
              <div className="text-sm text-slate-600">Total Applications</div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Excellent Match</p>
                  <p className="text-2xl font-bold text-green-600">
                    {applicants.filter(a => (a.match_score || 0) >= 80).length}
                  </p>
                </div>
                <Star className="w-8 h-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Good Match</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {applicants.filter(a => (a.match_score || 0) >= 60 && (a.match_score || 0) < 80).length}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Shortlisted</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {applicants.filter(a => a.status === 'shortlisted').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">New</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {applicants.filter(a => a.status === 'new').length}
                  </p>
                </div>
                <Users className="w-8 h-8 text-slate-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="match_score">Sort by Match Score</option>
                <option value="date">Sort by Date</option>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="reviewing">Reviewing</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="rejected">Rejected</option>
                <option value="hired">Hired</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Applicants List */}
        <div className="space-y-4">
          {filteredApplicants.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Applicants Yet</h3>
                <p className="text-slate-600">
                  {filterStatus !== 'all' ? 'No applicants match this filter.' : 'Applications will appear here once candidates apply.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredApplicants.map((applicant) => (
              <Card key={applicant.application_id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Applicant Info */}
                    <div className="lg:col-span-7">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">{applicant.applicant_name}</h3>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {getStatusBadge(applicant.status)}
                            {applicant.applied_via && (
                              <Badge variant="outline" className="text-xs">
                                {applicant.applied_via === 'platform' ? 'Platform' : 
                                 applicant.applied_via === 'linkedin' ? 'LinkedIn' : 'External'}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {applicant.match_score !== null && (
                          <div className="text-right">
                            <div className={`text-3xl font-bold ${getMatchScoreColor(applicant.match_score).split(' ')[0]}`}>
                              {applicant.match_score}%
                            </div>
                            <p className={`text-xs font-medium ${getMatchScoreColor(applicant.match_score).split(' ')[0]}`}>
                              {getMatchScoreLabel(applicant.match_score)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 text-sm text-slate-600 mb-4">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${applicant.applicant_email}`} className="hover:text-blue-600">
                            {applicant.applicant_email}
                          </a>
                        </div>
                        {applicant.applicant_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{applicant.applicant_phone}</span>
                          </div>
                        )}
                        {applicant.applicant_linkedin && (
                          <div className="flex items-center gap-2">
                            <Linkedin className="w-4 h-4" />
                            <a 
                              href={applicant.applicant_linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:text-blue-600"
                            >
                              View LinkedIn Profile
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>Applied {new Date(applicant.applied_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Skills Match */}
                      {applicant.skills_matched && applicant.skills_matched.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-slate-700 mb-2">Skills Matched:</p>
                          <div className="flex flex-wrap gap-2">
                            {applicant.skills_matched.map((skill, idx) => (
                              <Badge key={idx} className="bg-green-100 text-green-700 border-0 text-xs">
                                ✓ {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {applicant.skills_missing && applicant.skills_missing.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-700 mb-2">Skills Missing:</p>
                          <div className="flex flex-wrap gap-2">
                            {applicant.skills_missing.slice(0, 3).map((skill, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs text-slate-600">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Match Analysis */}
                      {applicant.match_analysis && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-slate-700">{applicant.match_analysis}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="lg:col-span-5 border-l border-slate-200 pl-6">
                      <h4 className="font-semibold text-sm text-slate-700 mb-3">Actions</h4>
                      
                      <div className="space-y-2">
                        {applicant.resume_url && (
                          <Button
                            onClick={() => handleDownloadResume(applicant.application_id, applicant.resume_filename)}
                            variant="outline"
                            size="sm"
                            className="w-full justify-start"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download Resume
                          </Button>
                        )}

                        <Button
                          onClick={() => setSelectedApplicant(applicant)}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </div>

                      <div className="border-t border-slate-200 my-4"></div>
                      
                      <h4 className="font-semibold text-sm text-slate-700 mb-3">Update Status</h4>
                      
                      <div className="space-y-2">
                        {applicant.status !== 'shortlisted' && (
                          <Button
                            onClick={() => handleUpdateStatus(applicant.application_id, 'shortlisted')}
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Shortlist
                          </Button>
                        )}
                        
                        {applicant.status !== 'rejected' && (
                          <Button
                            onClick={() => handleUpdateStatus(applicant.application_id, 'rejected')}
                            size="sm"
                            variant="outline"
                            className="w-full border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Cover Letter */}
                  {applicant.cover_letter && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <h4 className="font-semibold text-sm text-slate-700 mb-2">Cover Letter</h4>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{applicant.cover_letter}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicantsPage;
